"use client";

import { ConvexReactClient, useConvexAuth } from "convex/react";
import { ReactNode, useEffect } from "react";
import { ConvexClerkAuthProvider, useClerkAuthSnapshot } from "./convexClerkAuth";
import {
  initSubscriptionClientAuth,
  setAuthenticatedSubject,
} from "@/lib/participantPropertyAtomCreatorConvex";

const convexUrl = process.env.NEXT_PUBLIC_CONVEX_URL;

if (!convexUrl) {
  console.warn(
    "NEXT_PUBLIC_CONVEX_URL is not set. Convex hooks will fail until the environment variable is configured.",
  );
}

// Exported singleton - once ConvexClerkAuthProvider mounts, auth is set on this client
export const convexClient = convexUrl ? new ConvexReactClient(convexUrl) : null;

// Inner component to set up subscription client auth
function SubscriptionAuthSetup() {
  // The bridge's snapshot, not useAuth(): that suspends under Clerk v7's dynamic
  // provider, and the snapshot's getToken is already stable.
  const { getToken, userId } = useClerkAuthSnapshot() ?? {};
  const { isLoading, isAuthenticated } = useConvexAuth();

  useEffect(() => {
    if (!getToken) return;
    // Reapply auth when the Clerk subject changes so subscriptions are fenced immediately.
    setAuthenticatedSubject(isLoading ? undefined : isAuthenticated ? (userId ?? null) : null);
    initSubscriptionClientAuth(async () => {
      const token = await getToken({ template: "convex" });
      return token;
    });
  }, [getToken, isAuthenticated, isLoading, userId]);

  return null;
}

export function ConvexClientProvider({ children }: { children: ReactNode }) {
  if (!convexClient) {
    return <>{children}</>;
  }

  return (
    <ConvexClerkAuthProvider client={convexClient}>
      <SubscriptionAuthSetup />
      {children}
    </ConvexClerkAuthProvider>
  );
}
